import { useCallback, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { Leave, LeaveStatus, LeaveTypeId } from '@lms/shared-types'
import { ThinkingDots } from '../components/primitives/ThinkingDots'
import { FilterChip } from '../components/primitives/FilterChip'
import { YearNavigator } from '../components/primitives/YearNavigator'
import { CancelLeaveDialog } from '../components/employee/CancelLeaveDialog'
import {
  LeaveMetricCards,
  type LeaveStatusCounts,
} from '../components/employee/LeaveMetricCards'
import { LeaveHistoryTable } from '../components/employee/LeaveHistoryTable'
import { LEAVE_TYPE_LABEL } from '../lib/leave-type-meta'
import { downloadCsv, leavesToCsv } from '../lib/leaves-csv'
import { PaginationControls } from '../components/lists/PaginationControls'
import { ApiError } from '../api/client'
import { cancelLeave, createLeave, requestLeaveCancellation } from '../api/leaves'
import { flags } from '../lib/feature-flags'
import { leaveStartYear, leaveYearBounds } from '../lib/leave-year'
import { listEmployeeLeaves } from '../api/employees'
import { listMyLeaves } from '../api/me'
import { useSession } from '../auth/auth-context'
import { useEffectiveEmployeeId } from '../components/shell/effective-employee'
import { useToast } from '../components/primitives/Toast'
import {
  usePagedList,
  type PagedFetchArgs,
  type PagedFetchResult,
} from '../lib/use-paged-list'

/** Per-fetch metadata carried alongside the sliced rows (in the cached query
 *  data), so the visible page and everything derived from the whole history
 *  resolve together. */
interface LeavesMeta {
  /** Status counts across the chosen year (the whole history when the year
   *  filter is off), before the type/status filters. */
  metrics: LeaveStatusCounts
  /** Distinct leave types present in the chosen year, for the type chip row. */
  availableTypes: LeaveTypeId[]
  /** The full filtered set across every page — what the export writes. */
  filtered: Leave[]
  /** Leaves in the chosen year (before type/status filtering) — distinguishes
   *  "nothing this year" from "no rows match the filter". */
  allCount: number
  /** Whole history size, every year — distinguishes "no history at all" from
   *  "nothing in this year". */
  historyCount: number
}

// Stable empty references so `?? EMPTY_*` fallbacks don't churn identities.
const EMPTY_LEAVES: Leave[] = []
const EMPTY_TYPES: LeaveTypeId[] = []
const EMPTY_METRICS: LeaveStatusCounts = { pending: 0, approved: 0, rejected: 0 }

export function MyLeavePage(): JSX.Element {
  const { user } = useSession()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const openLeaveDetail = useCallback(
    (leaveId: string): void => {
      void navigate({ to: '/leave', search: { id: leaveId } })
    },
    [navigate],
  )

  const { notify } = useToast()
  const [pendingCancel, setPendingCancel] = useState<Leave | null>(null)
  const [cancelError, setCancelError] = useState<string | null>(null)

  const realId = user?.id ?? null
  const effectiveId = useEffectiveEmployeeId(realId)
  const isImpersonating = effectiveId !== realId

  const leavesEnabled = Boolean(effectiveId)

  // Year filter: a leave belongs to the year its start date falls in. Local
  // state like the other filters; defaults to the current year.
  const yearFilterOn = flags.leaveYearFilter
  const yearBounds = leaveYearBounds()
  const [year, setYear] = useState<number>(yearBounds.max)

  // History filter state — Set<…> for cheap toggling. Empty = no filter.
  const [typeFilter, setTypeFilter] = useState<ReadonlySet<LeaveTypeId>>(new Set())
  const [statusFilter, setStatusFilter] = useState<ReadonlySet<LeaveStatus>>(new Set())

  // Fetch the caller's FULL history newest-first. Everything the page shows —
  // status metrics, the type/status filters, the visible page, and the CSV
  // export — comes off this ONE fetch (the endpoint has no per-page/filter
  // params; it returns the whole, small history). Filtering a page slice — the
  // previous approach — silently scoped both the filter and the export to
  // whichever rows happened to be loaded.
  const fetchSortedHistory = useCallback(async (): Promise<Leave[]> => {
    const all =
      isImpersonating && effectiveId
        ? await listEmployeeLeaves(effectiveId)
        : await listMyLeaves()
    // Newest-first by createdAt — pending and decided leaves share one timeline,
    // matching the Lumen prototype's single My-leaves table.
    return [...all].sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''))
  }, [isImpersonating, effectiveId])

  // One paged query. Its fetcher computes the whole-history metrics + type
  // options + the full filtered set, and carries them in `meta` alongside the
  // sliced rows — so the visible page and everything derived from it resolve in
  // the SAME tick (no two-query race where the container renders before rows).
  // The filter signature rides in the key so changing a filter resets to page 1
  // and re-slices.
  const filterKey = `${yearFilterOn ? year : 'all'}|${[...typeFilter].sort().join(',')}|${[...statusFilter].sort().join(',')}`
  const fetchLeavesPaged = useCallback(
    async ({
      page,
      pageSize,
    }: PagedFetchArgs): Promise<PagedFetchResult<Leave, LeavesMeta>> => {
      const sorted = await fetchSortedHistory()
      const inYear = yearFilterOn
        ? sorted.filter((l) => leaveStartYear(l.startDate) === year)
        : sorted
      const metrics: LeaveStatusCounts = { pending: 0, approved: 0, rejected: 0 }
      const types = new Set<LeaveTypeId>()
      for (const l of inYear) {
        types.add(l.type)
        if (l.status === 'pending') metrics.pending += 1
        else if (l.status === 'approved') metrics.approved += 1
        else if (l.status === 'rejected') metrics.rejected += 1
      }
      const filtered =
        typeFilter.size === 0 && statusFilter.size === 0
          ? inYear
          : inYear.filter(
              (l) =>
                (typeFilter.size === 0 || typeFilter.has(l.type)) &&
                (statusFilter.size === 0 || statusFilter.has(l.status)),
            )
      const start = (page - 1) * pageSize
      return {
        rows: filtered.slice(start, start + pageSize),
        total: filtered.length,
        meta: {
          metrics,
          availableTypes: [...types].sort(),
          filtered,
          allCount: inYear.length,
          historyCount: sorted.length,
        },
      }
    },
    [fetchSortedHistory, typeFilter, statusFilter, yearFilterOn, year],
  )
  const leavesPaged = usePagedList<Leave, LeavesMeta>({
    queryKey: ['me', 'leaves', effectiveId, isImpersonating ? 'as' : 'self', filterKey],
    fetcher: fetchLeavesPaged,
    enabled: leavesEnabled,
  })

  const metrics = leavesPaged.meta?.metrics ?? EMPTY_METRICS
  const availableTypes = leavesPaged.meta?.availableTypes ?? EMPTY_TYPES
  // Full filtered set (all pages) — what the export writes.
  const filtered = leavesPaged.meta?.filtered ?? EMPTY_LEAVES
  const allCount = leavesPaged.meta?.allCount ?? 0
  const historyCount = leavesPaged.meta?.historyCount ?? 0
  const isLoading = leavesPaged.isLoading
  const isRefreshing = leavesPaged.isFetching

  const cancelMutation = useMutation({
    // An approved leave routes to the manager (request-cancellation); a
    // pending/escalated leave is withdrawn directly.
    mutationFn: (vars: { id: string; reason: string | undefined; isApproved: boolean }) =>
      vars.isApproved
        ? requestLeaveCancellation(vars.id, vars.reason ?? '')
        : cancelLeave(vars.id),
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Cancel failed'
      const status = err instanceof ApiError ? ` (${err.status})` : ''
      setCancelError(`${message}${status}`)
    },
    onSuccess: (_data, vars) => {
      const cancelled = filtered.find((l) => l.id === vars.id) ?? null
      setPendingCancel(null)
      setCancelError(null)
      if (vars.isApproved) {
        // Cancellation now needs approver sign-off — no immediate undo.
        notify('Cancellation request sent to your approver', 'success')
        return
      }
      if (cancelled) {
        notify('Leave cancelled', 'success', {
          label: 'Undo',
          onClick: () => {
            void createLeave({
              employeeId: cancelled.employeeId,
              type: cancelled.type,
              startDate: cancelled.startDate,
              endDate: cancelled.endDate,
              halfDay: cancelled.halfDay,
              halfStart: cancelled.halfStart ?? false,
              halfEnd: cancelled.halfEnd ?? false,
              reason: cancelled.reason,
              isEmergency: cancelled.isEmergency,
              expiry: cancelled.expiry,
            }).then(() => {
              void queryClient.invalidateQueries({ queryKey: ['me', 'leaves'] })
            })
          },
        })
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['me', 'leaves'] })
      void queryClient.invalidateQueries({ queryKey: ['me', 'balances'] })
    },
  })

  const toggleType = useCallback((t: LeaveTypeId) => {
    setTypeFilter((prev) => {
      const next = new Set(prev)
      if (next.has(t)) next.delete(t)
      else next.add(t)
      return next
    })
  }, [])
  const toggleStatus = useCallback((s: LeaveStatus) => {
    setStatusFilter((prev) => {
      const next = new Set(prev)
      if (next.has(s)) next.delete(s)
      else next.add(s)
      return next
    })
  }, [])
  const clearFilters = useCallback(() => {
    setTypeFilter(new Set())
    setStatusFilter(new Set())
  }, [])
  const anyFilterActive = typeFilter.size > 0 || statusFilter.size > 0

  // Export the ACTIVE filtered set (the whole thing, across every page) — not
  // just the loaded page.
  const handleExport = useCallback(() => {
    const csv = leavesToCsv(filtered)
    const stamp = new Date().toISOString().slice(0, 10)
    const scope = yearFilterOn ? `${year}-` : ''
    downloadCsv(`my-leaves-${scope}${stamp}.csv`, csv)
  }, [filtered, yearFilterOn, year])

  if (!user) {
    return (
      <div className="page" data-testid="my-leaves-page">
        <div className="between">
          <h1 className="h1">My leaves</h1>
        </div>
        <div
          className="card"
          style={{ padding: '24px 18px', color: 'var(--text-3)', fontSize: 14 }}
        >
          Sign in to see your leave.
        </div>
      </div>
    )
  }

  return (
    <>
      <div
        className="page"
        data-screen-label="Employee · My Leaves"
        data-testid="my-leaves-page"
      >
        <div className="between" style={{ marginBottom: 12 }}>
          <h1 className="h1">My leaves</h1>
          {isRefreshing ? <ThinkingDots label="Refreshing" /> : null}
        </div>

        {/* Toolbar row under the title: year control beside Export. */}
        <div data-testid="my-leaves-toolbar" style={toolbarStyle}>
          {anyFilterActive ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={clearFilters}
              data-testid="clear-filters"
            >
              Clear filters
            </button>
          ) : null}
          {yearFilterOn ? (
            <YearNavigator
              year={year}
              min={yearBounds.min}
              max={yearBounds.max}
              onChange={setYear}
            />
          ) : null}
          <button
            type="button"
            className="btn btn-soft btn-sm"
            onClick={handleExport}
            disabled={filtered.length === 0}
            style={{
              opacity: filtered.length === 0 ? 0.5 : 1,
              cursor: filtered.length === 0 ? 'not-allowed' : 'pointer',
            }}
            data-testid="export-button"
            title={
              anyFilterActive
                ? 'Export the filtered leaves as CSV'
                : yearFilterOn
                  ? `Export your ${year} leaves as CSV`
                  : 'Export all your leaves as CSV'
            }
          >
            <DownloadIcon />
            {anyFilterActive ? 'Export (filtered)' : 'Export'}
          </button>
        </div>

        {/* Status metrics — counts across the chosen year; each card toggles
            the matching status filter. Shown even at 0 (like HR Applied
            Leaves) so an empty year keeps the page's shape. */}
        {!isLoading ? (
          <LeaveMetricCards
            counts={metrics}
            selectedStatuses={statusFilter}
            onToggleStatus={toggleStatus}
          />
        ) : null}

        {/* Leave-type multiselect — one chip row (only types actually present).
            Clearing lives in the header next to Export. */}
        {!isLoading && availableTypes.length > 0 ? (
          <div
            data-testid="leave-type-filter"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 16,
            }}
          >
            <span style={typeLabelStyle}>Type</span>
            {availableTypes.map((t) => (
              <FilterChip
                key={t}
                label={LEAVE_TYPE_LABEL[t] ?? t}
                active={typeFilter.has(t)}
                onClick={() => toggleType(t)}
                style={{ minHeight: 32, fontSize: 12 }}
              />
            ))}
          </div>
        ) : null}

        {isLoading ? (
          <div className="placeholder" data-testid="leave-history-loading">
            Loading your leave history…
          </div>
        ) : leavesPaged.rows.length === 0 ? (
          <div className="placeholder" data-testid="leave-history-empty">
            {historyCount === 0
              ? isImpersonating
                ? 'No leave history for this employee.'
                : 'No leave history yet. The form is there when you need it.'
              : allCount === 0
                ? `No leaves in ${year}.`
                : 'No leaves match these filters.'}
          </div>
        ) : (
          <div data-testid="leave-history">
            <LeaveHistoryTable
              leaves={leavesPaged.rows}
              onOpen={openLeaveDetail}
              onCancel={(leave) => setPendingCancel(leave)}
              cancellable={!isImpersonating}
              allowRequestCancellation={flags.cancelApprovedRequiresApproval}
              ownerView
              footer={
                <PaginationControls
                  page={leavesPaged.page}
                  pageSize={leavesPaged.pageSize}
                  total={leavesPaged.total}
                  totalPages={leavesPaged.totalPages}
                  pageSizeOptions={leavesPaged.pageSizeOptions}
                  rangeStart={leavesPaged.rangeStart}
                  rangeEnd={leavesPaged.rangeEnd}
                  canPrev={leavesPaged.canPrev}
                  canNext={leavesPaged.canNext}
                  onPageChange={leavesPaged.setPage}
                  onPageSizeChange={leavesPaged.setPageSize}
                  isFetching={leavesPaged.isFetching}
                />
              }
            />
          </div>
        )}
      </div>

      {pendingCancel != null && (
        <CancelLeaveDialog
          open={Boolean(pendingCancel)}
          leave={pendingCancel}
          onConfirm={(reason) =>
            cancelMutation.mutate({
              id: pendingCancel.id,
              reason,
              isApproved: pendingCancel.status === 'approved',
            })
          }
          onClose={() => {
            if (!cancelMutation.isPending) {
              setPendingCancel(null)
              setCancelError(null)
            }
          }}
          isPending={cancelMutation.isPending}
          error={cancelError}
        />
      )}
    </>
  )
}

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  marginBottom: 16,
}

const typeLabelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 10.5,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: 'var(--text-3)',
  fontWeight: 600,
  paddingRight: 4,
}

// ── Icons (Lumen prototype set) ──────────────────────────────────────────────

function DownloadIcon(): JSX.Element {
  return (
    <svg
      aria-hidden
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}
